import { BaseEffect } from '../BaseEffect.js';

/**
 * Drive Home effect implementation
 * Based on "The Drive Home" by Martijn Steinrucken
 * Creates an atmospheric rainy night drive scene with bokeh lights
 */
export class DriveHomeEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            speed: 1.0,           // Animation speed
            rainIntensity: 0.5,   // Rain intensity
            bokehSize: 1.0,       // Size of light bokeh
            bokehBlur: 0.5,       // Blur amount of bokeh
            cameraShake: 0.5,     // Amount of camera shake
            streetLightColor: [1.0, 0.7, 0.3],  // Street light color
            headLightColor: [0.8, 0.8, 1.0],    // Head light color
            tailLightColor: [1.0, 0.1, 0.1],    // Tail light color
            blendAmount: 0.8,     // Amount to blend with source image
            animate: true         // Whether to animate the effect
        };

        super('drive-home', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_speed;
            uniform float u_rainIntensity;
            uniform float u_bokehSize;
            uniform float u_bokehBlur;
            uniform float u_cameraShake;
            uniform vec3 u_streetLightColor;
            uniform vec3 u_headLightColor;
            uniform vec3 u_tailLightColor;
            uniform float u_blendAmount;
            
            varying vec2 v_texcoord;

            #define S(x, y, z) smoothstep(x, y, z)
            #define sat(x) clamp(x, 0., 1.)

            float N21(float n) {
                return fract(sin(n*123.456)*43758.5453123);
            }

            vec3 N31(float p) {
                vec3 p3 = fract(vec3(p) * vec3(.1031,.11369,.13787));
                p3 += dot(p3, p3.yzx + 19.19);
                return fract(vec3((p3.x + p3.y)*p3.z, (p3.x+p3.z)*p3.y, (p3.y+p3.z)*p3.x));
            }

            float BokehMask(vec2 uv, vec2 pos, float size, float blur) {
                vec2 d = uv - pos;
                float r = length(d);
                float c = smoothstep(size, size*(1.0-blur), r);
                c *= mix(0.6, 1.0, smoothstep(size*0.8, size, r));
                return c;
            }

            vec2 GetRainDrops(vec2 uv, float t) {
                vec2 o = vec2(0.);
                
                uv *= vec2(10., 2.5)*2.;
                vec2 id = floor(uv);
                vec2 gv = fract(uv)-.5;
                
                vec3 n = N31(id.x + (id.y)*546.3524);
                
                t *= u_speed;
                float y = sin(t+n.x*6.2831)*0.5;
                vec2 p = vec2(0., y);
                
                gv.y *= 4.;
                p.y *= 4.;
                
                float d = length(gv-p);
                
                float rain = smoothstep(.2, .1, d);
                rain *= smoothstep(-.1, .1, gv.y-p.y);
                
                o = gv*rain;
                
                return o * u_rainIntensity;
            }

            vec3 StreetLights(vec2 uv, float t) {
                vec3 col = vec3(0.);
                
                for(float i=0.; i<3.; i++) {
                    float z = fract(t*.1 + i*.277);
                    float size = mix(.05, .1, z)*u_bokehSize;
                    float blur = .1*u_bokehBlur;
                    
                    vec2 pos = vec2(sin(z*6.28)*.5, z);
                    float m = BokehMask(uv, pos, size, blur);
                    
                    col += u_streetLightColor * m * (1.-z);
                }
                
                return col;
            }

            vec3 CarLights(vec2 uv, float t) {
                vec3 col = vec3(0.);
                
                // Oncoming traffic (head lights)
                for(float i=0.; i<3.; i++) {
                    float z = fract(t*.2 + i*.277);
                    float size = mix(.02, .05, z)*u_bokehSize;
                    float blur = .1*u_bokehBlur;
                    
                    vec2 pos = vec2(.3+sin(z*6.28)*.1, z);
                    float m = BokehMask(uv, pos, size, blur);
                    m += BokehMask(uv, pos+vec2(.1,0.), size, blur);
                    
                    col += u_headLightColor * m * (1.-z);
                }
                
                // Tail lights ahead
                for(float i=0.; i<2.; i++) {
                    float z = fract(t*.15 + i*.277+.5);
                    float size = mix(.02, .04, z)*u_bokehSize;
                    float blur = .1*u_bokehBlur;
                    
                    vec2 pos = vec2(-.3+sin(z*6.28)*.1, z);
                    float m = BokehMask(uv, pos, size, blur);
                    m += BokehMask(uv, pos+vec2(.08,0.), size, blur);
                    
                    col += u_tailLightColor * m * (1.-z);
                }
                
                return col;
            }

            void main() {
                vec2 uv = v_texcoord;
                uv = uv * 2.0 - 1.0;
                uv.x *= u_resolution.x/u_resolution.y;
                
                float t = u_time * u_speed;
                
                // Camera shake
                float shake = sin(t*10.)*.002*u_cameraShake;
                uv.y += shake;
                
                // Get rain drops
                vec2 rainOffset = GetRainDrops(uv, t);
                uv += rainOffset * 0.1;
                
                // Accumulate lights
                vec3 col = StreetLights(uv, t);
                col += CarLights(uv, t);
                
                // Add slight blue tint to dark areas
                col += vec3(.1, .1, .15) * (1.-length(col));
                
                // Blend with source image
                vec4 sourceColor = texture2D(u_image, v_texcoord);
                vec3 finalColor = mix(sourceColor.rgb, col, u_blendAmount);
                
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_image', { type: 'sampler2D' });
        this.addUniform('u_time', { type: '1f' });
        this.addUniform('u_resolution', { type: '2f' });
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_rainIntensity', { type: '1f' });
        this.addUniform('u_bokehSize', { type: '1f' });
        this.addUniform('u_bokehBlur', { type: '1f' });
        this.addUniform('u_cameraShake', { type: '1f' });
        this.addUniform('u_streetLightColor', { type: '3fv' });
        this.addUniform('u_headLightColor', { type: '3fv' });
        this.addUniform('u_tailLightColor', { type: '3fv' });
        this.addUniform('u_blendAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_speed: { value: this.params.speed, type: '1f' },
            u_rainIntensity: { value: this.params.rainIntensity, type: '1f' },
            u_bokehSize: { value: this.params.bokehSize, type: '1f' },
            u_bokehBlur: { value: this.params.bokehBlur, type: '1f' },
            u_cameraShake: { value: this.params.cameraShake, type: '1f' },
            u_streetLightColor: { value: this.params.streetLightColor, type: '3fv' },
            u_headLightColor: { value: this.params.headLightColor, type: '3fv' },
            u_tailLightColor: { value: this.params.tailLightColor, type: '3fv' },
            u_blendAmount: { value: this.params.blendAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            blendAmount: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Effect Strength'
            },
            speed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            rainIntensity: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Rain Intensity'
            },
            bokehSize: {
                min: 0.1,
                max: 2,
                step: 0.1,
                label: 'Light Size'
            },
            bokehBlur: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Light Blur'
            },
            cameraShake: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Camera Shake'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 